import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import './Ranking.css';

/**
 * Página de Ranking
 */

const Ranking = () => {
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tipoRanking, setTipoRanking] = useState('geral'); // 'geral' ou 'rodada'
  const [rodadaSelecionada, setRodadaSelecionada] = useState('');
  const [rodadas, setRodadas] = useState([]);
  const [fraseRanking, setFraseRanking] = useState('🏆 Ranking de Pontos');

  useEffect(() => {
    carregarRodadas();
    carregarRanking();
    carregarTemaAtivo();
  }, []);

  const carregarTemaAtivo = async () => {
    try {
      const response = await apiClient.get('/temas/ativo');
      if (response.data?.frase_ranking) {
        setFraseRanking(response.data.frase_ranking);
      }
    } catch (err) {
      console.error('Erro ao carregar tema:', err);
    }
  };

  useEffect(() => {
    if (tipoRanking === 'geral') {
      carregarRanking();
    } else if (tipoRanking === 'rodada' && rodadaSelecionada) {
      carregarRankingPorRodada(rodadaSelecionada);
    }
  }, [tipoRanking, rodadaSelecionada]);

  const carregarRodadas = async () => {
    try {
      const response = await apiClient.get('/rodadas');
      // Incluir rodadas abertas e fechadas, ordenar por data (mais recente primeiro)
      // Rodadas abertas primeiro, depois fechadas
      const todasRodadas = (response.data || [])
        .sort((a, b) => {
          // Primeiro ordenar por status (aberta primeiro)
          if (a.status === 'aberta' && b.status !== 'aberta') return -1;
          if (a.status !== 'aberta' && b.status === 'aberta') return 1;
          // Depois por data (mais recente primeiro)
          return new Date(b.data) - new Date(a.data);
        });
      setRodadas(todasRodadas);
      
      // Se não há rodada selecionada e há rodadas, selecionar a primeira
      if (!rodadaSelecionada && todasRodadas.length > 0) {
        setRodadaSelecionada(todasRodadas[0].id);
      }
    } catch (err) {
      console.error('Erro ao carregar rodadas:', err);
    }
  };

  const carregarRanking = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/ranking');
      setRanking(response.data);
      setError('');
    } catch (err) {
      console.error('Erro ao carregar ranking:', err);
      setError(err.response?.data?.error || 'Erro ao carregar ranking');
    } finally {
      setLoading(false);
    }
  };

  const [rodadaInfo, setRodadaInfo] = useState(null);

  const carregarRankingPorRodada = async (rodadaId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/ranking/rodada/${rodadaId}`);
      // Nova estrutura: { ranking: [...], rodada: {...} }
      if (response.data.ranking) {
        setRanking(response.data.ranking);
        setRodadaInfo(response.data.rodada);
      } else {
        // Compatibilidade com estrutura antiga
        setRanking(response.data);
        setRodadaInfo(null);
      }
      setError('');
    } catch (err) {
      console.error('Erro ao carregar ranking da rodada:', err);
      setError(err.response?.data?.error || 'Erro ao carregar ranking da rodada');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  return (
    <div className="container ranking-page">
      <div className="page-header tolkien-ranking-theme">
        <div className="theme-header">
          <span className="tolkien-icon">💍</span>
          <h1>{fraseRanking}</h1>
          <span className="tolkien-icon">⚔️</span>
        </div>
      </div>

      {/* Seletor de Tipo de Ranking */}
      <div className="ranking-selector">
        <div className="selector-group">
          <label>
            <input
              type="radio"
              name="tipoRanking"
              value="geral"
              checked={tipoRanking === 'geral'}
              onChange={(e) => setTipoRanking(e.target.value)}
            />
            <span>Ranking Geral</span>
          </label>
          <label>
            <input
              type="radio"
              name="tipoRanking"
              value="rodada"
              checked={tipoRanking === 'rodada'}
              onChange={(e) => setTipoRanking(e.target.value)}
            />
            <span>Ranking por Rodada</span>
          </label>
        </div>

        {tipoRanking === 'rodada' && (
          <div className="rodada-selector">
            <label htmlFor="rodada-select">Selecione a Rodada:</label>
            <select
              id="rodada-select"
              value={rodadaSelecionada}
              onChange={(e) => setRodadaSelecionada(e.target.value)}
            >
              <option value="">Selecione uma rodada...</option>
              {rodadas.map(rodada => (
                <option key={rodada.id} value={rodada.id}>
                  {rodada.status === 'aberta' ? '🟢 ' : '🔴 '}
                  {new Date(rodada.data).toLocaleDateString('pt-BR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  {rodada.status === 'aberta' ? ' (Rodada Aberta)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {error && <div className="error">{error}</div>}

      {tipoRanking === 'rodada' && rodadaInfo && rodadaInfo.status === 'aberta' && (
        <div className="info-box" style={{ 
          marginBottom: '20px', 
          padding: '15px', 
          background: '#fff3cd', 
          borderLeft: '4px solid #ffc107',
          borderRadius: '8px'
        }}>
          <p style={{ margin: 0, fontSize: '0.95em' }}>
            <strong>⚠️ Rodada Aberta:</strong> As posições e pontuações mostradas aqui são temporárias e podem mudar até o fechamento da rodada.
          </p>
        </div>
      )}

      {tipoRanking === 'rodada' && !rodadaSelecionada ? (
        <div className="no-data">
          <p>Selecione uma rodada para ver o ranking.</p>
        </div>
      ) : ranking.length === 0 ? (
        <div className="no-data">
          <p>
            {tipoRanking === 'geral' 
              ? 'Nenhum jogador com pontos ainda. As pontuações aparecerão aqui após o fechamento das rodadas.'
              : 'Nenhum jogador com pontos nesta rodada.'}
          </p>
        </div>
      ) : (
        <div className="ranking-container">
          {tipoRanking === 'rodada' && rodadaInfo && (
            <div style={{ marginBottom: '15px', textAlign: 'center' }}>
              <h3 style={{ color: 'white', margin: 0 }}>
                {new Date(rodadaInfo.data).toLocaleDateString('pt-BR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                {rodadaInfo.status === 'aberta' && ' *'}
              </h3>
              {rodadaInfo.status === 'aberta' && (
                <p style={{ color: '#ffc107', margin: '5px 0 0 0', fontSize: '0.9em' }}>
                  * Rodada ainda não fechada
                </p>
              )}
            </div>
          )}
          <table className="ranking-table">
            <thead>
              <tr>
                <th>Posição</th>
                <th>Jogador</th>
                <th>Total de Pontos</th>
                {tipoRanking === 'geral' ? <th>Rodadas</th> : <th>Posição Final</th>}
              </tr>
            </thead>
            <tbody>
              {ranking.map((item, index) => (
                <tr key={item.jogador.id} className={index < 3 ? `top-${index + 1}` : ''}>
                  <td className="posicao">
                    {index === 0 && '🥇'}
                    {index === 1 && '🥈'}
                    {index === 2 && '🥉'}
                    {index > 2 && `${item.posicao}º`}
                  </td>
                  <td className="jogador">{item.jogador.nome}</td>
                  <td className="pontos"><strong>{item.total_pontos}</strong> pontos</td>
                  {tipoRanking === 'geral' ? (
                    <td className="rodadas">{item.total_rodadas} rodada(s)</td>
                  ) : (
                    <td className="rodadas">{item.posicao_final}º lugar</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Ranking;
